import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { MutationCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { App } from './App.js';
import { Esiti } from './components/Esiti.js';
import { eUnaDomanda, esitoDelGuasto, esitoRiuscito, mostraEsito } from './lib/esiti.js';
import './styles/base.css';

// ⚖️ Lo stato del server NON si duplica in uno stato locale: TanStack Query è
// l'unica copia. Due copie degli stessi dati divergono sempre, e divergono in
// silenzio — il difetto si vede tre schermate più in là, come un numero
// sbagliato che nessuno sa da dove venga.
const queryClient = new QueryClient({
  // 🆕 26/09/2026 — OGNI AZIONE DICE COM'È ANDATA, e lo dice da qui: un posto
  // solo vede passare tutte le `useMutation`, anche quelle che verranno.
  // 🗣️ *«ci deve essere un avviso se è andata a buon fine o no … Qualsiasi
  // tipo di operazione»*. Le tre forme dell'avviso stanno in `lib/esiti.ts`.
  mutationCache: new MutationCache({
    onSuccess: (dati, variabili, _ctx, azione) => {
      const esito = esitoRiuscito(azione.meta, dati, variabili);
      if (esito !== null) mostraEsito(esito);
    },
    onError: (errore, _variabili, _ctx, azione) => {
      if (eUnaDomanda(azione.meta, errore)) return;
      mostraEsito(esitoDelGuasto(errore));
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      // ⛔ Un 401 o un 403 non si ritentano: il primo lo cura il rinnovo del
      // gettone dentro `lib/api.ts`, il secondo è una risposta definitiva, e
      // ripeterla tre volte non la cambia — riempie solo il registro del
      // server di rifiuti identici.
      retry: (quante, errore) => {
        const stato = (errore as { stato?: number }).stato;
        if (stato === 401 || stato === 403 || stato === 429) return false;
        return quante < 1;
      },
      refetchOnWindowFocus: true,
    },
  },
});

const root = document.getElementById('root');
if (!root) throw new Error('Manca #root: index.html non è quello previsto');

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
      <Esiti />
    </QueryClientProvider>
  </StrictMode>,
);
